/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useExtensionForm.ts
 🗓️ Created: 2025-01-15
 🧩 Description:
    Custom hook for managing extension form state, validation, and submission.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import { createExtension, updateExtension } from '../api/extensionsApi';
import { buildSettings } from '../utils/settingsHelpers';
import type {
  ExtensionFormState,
  VoicemailSettingsState,
  RecordingSettingsState,
  ContactInfoState,
  FollowMeSettingsState,
  ValidationErrors,
} from '../types';

interface UseExtensionFormOptions {
  formData: ExtensionFormState;
  setFormData: React.Dispatch<React.SetStateAction<ExtensionFormState>>;
  voicemailData: VoicemailSettingsState;
  setVoicemailData: React.Dispatch<React.SetStateAction<VoicemailSettingsState>>;
  recordingSettings: RecordingSettingsState;
  setRecordingSettings: React.Dispatch<React.SetStateAction<RecordingSettingsState>>;
  contactInfo: ContactInfoState;
  setContactInfo: React.Dispatch<React.SetStateAction<ContactInfoState>>;
  followMeData: FollowMeSettingsState;
  setFollowMeData: React.Dispatch<React.SetStateAction<FollowMeSettingsState>>;
  originalFormData: ExtensionFormState;
  originalVoicemailData: VoicemailSettingsState;
  originalRecordingSettings: RecordingSettingsState;
  originalContactInfo: ContactInfoState;
  originalFollowMeData: FollowMeSettingsState;
  isEditMode: boolean;
  extensionId?: string;
}

export function useExtensionForm({
  formData,
  setFormData,
  voicemailData,
  setVoicemailData,
  recordingSettings,
  setRecordingSettings,
  contactInfo,
  setContactInfo,
  followMeData,
  setFollowMeData,
  originalFormData,
  originalVoicemailData,
  originalRecordingSettings,
  originalContactInfo,
  originalFollowMeData,
  isEditMode,
  extensionId,
}: UseExtensionFormOptions) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showSuccess, showError, showInfo } = useNotifications();
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isSaving, setIsSaving] = useState(false);

  // Check if there are unsaved changes
  const hasChanges = useMemo(() => {
    const formChanged = JSON.stringify(formData) !== JSON.stringify(originalFormData);
    const voicemailChanged = JSON.stringify(voicemailData) !== JSON.stringify(originalVoicemailData);
    const recordingChanged = JSON.stringify(recordingSettings) !== JSON.stringify(originalRecordingSettings);
    const contactChanged = JSON.stringify(contactInfo) !== JSON.stringify(originalContactInfo);
    const followMeChanged = JSON.stringify(followMeData) !== JSON.stringify(originalFollowMeData);

    return formChanged || voicemailChanged || recordingChanged || contactChanged || followMeChanged;
  }, [
    formData,
    originalFormData,
    voicemailData,
    originalVoicemailData,
    recordingSettings,
    originalRecordingSettings,
    contactInfo,
    originalContactInfo,
    followMeData,
    originalFollowMeData,
  ]);

  // Auto-sync User ID when extension changes
  useEffect(() => {
    if (formData.extension_type === 'sip' && formData.extension) {
      setFormData(prev => ({
        ...prev,
        deviceUsername: formData.extension,
      }));
    }
  }, [formData.extension, formData.extension_type, setFormData]);

  // Auto-sync Description when name changes (only for new extensions)
  useEffect(() => {
    if (formData.extension_type === 'sip' && formData.name && !isEditMode) {
      setFormData(prev => ({
        ...prev,
        deviceDescription: formData.name,
      }));
    }
  }, [formData.name, formData.extension_type, isEditMode, setFormData]);

  // Validation
  const validateForm = useCallback(() => {
    const newErrors: ValidationErrors = {};

    // Auto-fill Caller ID fields if they are empty
    const updatedFormData = { ...formData };
    let needsUpdate = false;

    // Auto-fill Internal Caller ID if empty
    if (!updatedFormData.internalCidName.trim() && formData.name.trim()) {
      updatedFormData.internalCidName = formData.name;
      needsUpdate = true;
    }
    if (!updatedFormData.internalCidNumber.trim() && formData.extension.trim()) {
      updatedFormData.internalCidNumber = formData.extension;
      needsUpdate = true;
    }

    // Auto-fill External Caller ID if empty
    if (!updatedFormData.externalCidName.trim() && formData.name.trim()) {
      updatedFormData.externalCidName = formData.name;
      needsUpdate = true;
    }
    if (!updatedFormData.externalCidNumber.trim() && formData.extension.trim()) {
      updatedFormData.externalCidNumber = formData.extension;
      needsUpdate = true;
    }

    // Auto-fill Emergency Caller ID if empty
    if (!updatedFormData.emergencyCidName.trim() && formData.name.trim()) {
      updatedFormData.emergencyCidName = formData.name;
      needsUpdate = true;
    }
    if (!updatedFormData.emergencyCidNumber.trim() && formData.extension.trim()) {
      updatedFormData.emergencyCidNumber = formData.extension;
      needsUpdate = true;
    }

    // Update form data if any fields were auto-filled
    if (needsUpdate) {
      setFormData(updatedFormData);
    }

    // Validate required fields
    if (!updatedFormData.extension.trim()) {
      newErrors.extension = t('extensions.validation.extensionRequired');
    }

    if (!updatedFormData.name.trim()) {
      newErrors.name = t('extensions.validation.nameRequired');
    }

    // Validate Class of Service is assigned
    if (!updatedFormData.classOfServicesId) {
      newErrors.classOfServicesId = t('extensions.validation.classOfServiceRequired');
    }

    // Validate device fields for SIP type
    if (updatedFormData.extension_type === 'sip') {
      if (!updatedFormData.deviceUsername?.trim()) {
        newErrors.deviceUsername = t('extensions.validation.deviceUsernameRequired');
      }
      if (!updatedFormData.devicePassword?.trim()) {
        newErrors.devicePassword = t('extensions.validation.devicePasswordRequired');
      }
      if (!updatedFormData.deviceDescription?.trim()) {
        newErrors.deviceDescription = t('extensions.validation.deviceDescriptionRequired');
      }
    }

    // Validate virtual configuration for Virtual type
    if (updatedFormData.extension_type === 'virtual') {
      if (!updatedFormData.forwardTo?.trim()) {
        newErrors.forwardTo = t('extensions.validation.forwardToRequired');
      }
    }

    // Validate voicemail
    if (formData.voicemailEnabled && !voicemailData.password.trim()) {
      newErrors.vmPassword = t('extensions.validation.passwordRequired');
    }

    if (voicemailData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(voicemailData.email)) {
      newErrors.vmEmail = t('extensions.validation.emailInvalid');
    }

    setErrors(newErrors);

    return {
      isValid: Object.keys(newErrors).length === 0,
      errors: newErrors,
      updatedFormData: updatedFormData,
    };
  }, [formData, voicemailData, t, setFormData]);

  // Handle save - Always-Active Save Standard: always attempt to save
  const handleSave = useCallback(async () => {
    const validation = validateForm();

    if (!validation.isValid) {
      // Find which tab has errors and navigate to it
      const errorKeys = Object.keys(validation.errors);

      let baseErrorMsg = t('extensions.messages.validationError');

      if (errorKeys.some(key => key.startsWith('vm'))) {
        baseErrorMsg = t('extensions.messages.voicemailValidationError');
      } else if (errorKeys.some(key => key.includes('device') || key.includes('Device'))) {
        baseErrorMsg = t('extensions.messages.deviceValidationError');
      }

      const errorMsgs = Object.values(validation.errors).filter(msg => typeof msg === 'string').join(' • ');
      showError(`${baseErrorMsg}${errorMsgs ? ': ' + errorMsgs : ''}`);
      return undefined;
    }

    setIsSaving(true);
    try {
      const dataToSave = validation.updatedFormData || formData;
      const settings = buildSettings(dataToSave, recordingSettings, contactInfo, followMeData);

      const payload: {
        extension: string;
        name: string;
        extension_type: string;
        languageCode: string;
        enabled: boolean;
        classOfServicesId: string | number | null;
        dialProfileId: string | number | null;
        callCenterAgentProfileId: string | number | null;
        billingEnabled: boolean;
        voicemailEnabled: boolean;
        blfSupervisionEnabled: boolean;
        maxCalls: number;
        portalEnabled: boolean;
        portalUser: string;
        portalPassword: string;
        softphoneEnabled: boolean;
        smsNumberId: number | string | null;
        chatbotEnabled: boolean;
        chatbotAgentId: number | null;
        simultaneousTranslationEnabled: boolean;
        aiTranslatorProfileId: number | null;
        faxEnabled: boolean;
        faxNumber: string | null;
        faxEmailTo: string | null;
        faxEmailFrom: string | null;
        faxDeviceId: number | null;
        forwardTo: string | null;
        settings: unknown;
        voicemail?: {
          password: string; email: string; maxMessages: number;
          notificationEnabled: boolean; transcribeEnabled: boolean;
          emailAttachment: boolean; language: string; timezone: string;
          greeting: string; storagePath: string; emailTemplate: string;
        };
        devices?: Array<{ username: string; password: string; description: string; sipProfileId: number | null; dispatchLocationId: number | null }>;
      } = {
        extension: dataToSave.extension,
        name: dataToSave.name,
        extension_type: dataToSave.extension_type,
        languageCode: dataToSave.languageCode,
        enabled: dataToSave.enabled,
        classOfServicesId: dataToSave.classOfServicesId,
        dialProfileId: dataToSave.dialProfileId,
        callCenterAgentProfileId: dataToSave.callCenterAgentProfileId,
        billingEnabled: dataToSave.billingEnabled,
        voicemailEnabled: dataToSave.voicemailEnabled,
        blfSupervisionEnabled: dataToSave.blfSupervisionEnabled,
        maxCalls: dataToSave.maxCalls || 0,
        // Portal Access
        portalEnabled: dataToSave.portalEnabled,
        portalUser: dataToSave.portalUser,
        portalPassword: dataToSave.portalPassword,
        softphoneEnabled: dataToSave.softphoneEnabled,
        // SMS Integration
        smsNumberId: dataToSave.smsNumberId,
        // AI Chatbot
        chatbotEnabled: dataToSave.chatbotEnabled ?? false,
        chatbotAgentId: dataToSave.chatbotAgentId || null,
        // AI Translator
        simultaneousTranslationEnabled: dataToSave.simultaneousTranslationEnabled ?? false,
        aiTranslatorProfileId: dataToSave.aiTranslatorProfileId || null,
        // Fax (inline — stored directly on sip_extensions)
        faxEnabled: dataToSave.faxEnabled ?? false,
        faxNumber: dataToSave.faxNumber || null,
        faxEmailTo: dataToSave.faxEmailTo || null,
        faxEmailFrom: dataToSave.faxEmailFrom || null,
        faxDeviceId: dataToSave.faxDeviceId || null,
        // Virtual Extension destination
        forwardTo: dataToSave.forwardTo || null,
        settings: settings,
      };

      // Add voicemail data if enabled
      if (dataToSave.voicemailEnabled) {
        payload.voicemail = {
          password: voicemailData.password,
          email: voicemailData.email,
          maxMessages: voicemailData.maxMessages,
          notificationEnabled: voicemailData.notificationEnabled,
          transcribeEnabled: voicemailData.transcribeEnabled,
          emailAttachment: voicemailData.emailAttachment,
          language: voicemailData.language,
          timezone: voicemailData.timezone,
          greeting: voicemailData.greeting,
          storagePath: voicemailData.storagePath,
          emailTemplate: voicemailData.emailTemplate,
        };
      }


      // Add device data for SIP extensions
      if (dataToSave.extension_type === 'sip') {
        payload.devices = [{
          username: dataToSave.deviceUsername,
          password: dataToSave.devicePassword,
          description: dataToSave.deviceDescription,
          sipProfileId: dataToSave.deviceSipProfileId,
          dispatchLocationId: dataToSave.deviceDispatchLocationId || null,
        }];
      }

      let savedExtension;
      if (isEditMode && extensionId) {
        savedExtension = await updateExtension(extensionId, payload);
        showSuccess(t('extensions.messages.updateSuccess'));
      } else {
        savedExtension = await createExtension(payload);
        showSuccess(t('extensions.messages.createSuccess'));
        // Note: Navigation after create is now handled by the dropdown actions
        // in ExtensionFormView, so we don't navigate here automatically
      }

      return savedExtension;
    } catch (error) {
      console.error('Error saving extension:', error);
      const msg = error instanceof Error ? error.message : t('extensions.messages.saveError');
      showError(msg);
    } finally {
      setIsSaving(false);
    }
  }, [
    formData,
    voicemailData,
    recordingSettings,
    contactInfo,
    followMeData,
    validateForm,
    isEditMode,
    extensionId,
    showSuccess,
    showError,
    t,
  ]);

  // Handle cancel - revert changes and stay on form (no confirmation needed)
  const handleCancel = useCallback(() => {
    if (hasChanges) {
      // Revert all data to original values
      setFormData({ ...originalFormData });
      setVoicemailData({ ...originalVoicemailData });
      setRecordingSettings({ ...originalRecordingSettings });
      setContactInfo({ ...originalContactInfo });
      setFollowMeData({ ...originalFollowMeData });
      setErrors({});
    }
    // If no changes, do nothing (stay on form)
  }, [hasChanges, setFormData, setVoicemailData, setRecordingSettings, setContactInfo, setFollowMeData, originalFormData, originalVoicemailData, originalRecordingSettings, originalContactInfo, originalFollowMeData]);

  return {
    errors,
    setErrors,
    isSaving,
    hasChanges,
    validateForm,
    handleSave,
    handleCancel,
  };
}

